<script lang="ts">
  import type { Snippet } from 'svelte';

  interface Props {
    children: Snippet;
    /** Light theme renders Chrome's default light chrome; dark renders the black chrome. */
    theme?: 'light' | 'dark';
    /** URL displayed in the address bar. */
    url?: string;
    /** Active tab title. */
    tabTitle?: string;
    /** Optional second tab. Pass `null` to hide. */
    secondTab?: string | null;
    /** Show the lock icon next to the URL. */
    secure?: boolean;
    /** Extra class on the outer wrapper. */
    class?: string;
    /** Drop the outer shadow (useful when the parent supplies its own). */
    flat?: boolean;
    /** Header chrome density. 'md' (default) is true-to-Chrome at 86px total;
     *  'sm' compacts to ~64px so the chrome takes less proportional space when
     *  the frame is rendered large (used in the hero). */
    size?: 'sm' | 'md';
  }

  let {
    children,
    theme = 'light',
    url = 'app.corral.ai',
    tabTitle = 'Corral',
    secondTab = 'New Tab',
    secure = true,
    class: className = '',
    flat = false,
    size = 'md',
  }: Props = $props();

  // Palette derived from Figma file KVZcQgocc6VrPghKVOBGye, nodes 102:772 (light) and 102:855 (dark).
  const palette = $derived(
    theme === 'dark'
      ? {
          border: '#1f1f1f',
          headerBg: '#2b2b2b',
          tabsGradFrom: '#3a3a3a',
          tabsGradTo: '#2b2b2b',
          activeTabBg: '#3a3a3a',
          baseBg: '#3a3a3a',
          baseBorder: '#444746',
          urlBg: '#282828',
          urlText: '#c7c7c7',
          tabText: '#c7c7c7',
          icon: '#c7c7c7',
          iconMuted: '#84888c',
          divider: '#5e5e5e',
          contentBg: '#202124',
        }
      : {
          border: '#d3d3d3',
          headerBg: '#dcdcdc',
          tabsGradFrom: '#ffffff',
          tabsGradTo: '#dcdcdc',
          activeTabBg: '#ffffff',
          baseBg: '#ffffff',
          baseBorder: '#e3e3e3',
          urlBg: '#f1f3f4',
          urlText: '#3c4043',
          tabText: '#3c4043',
          icon: '#5f6368',
          iconMuted: '#84888c',
          divider: '#bdc1c6',
          contentBg: '#ffffff',
        }
  );
</script>

<!--
  ChromeFrame — Windows-style Chrome browser chrome (window controls top-right,
  no traffic lights). Rebuilt as CSS+SVG from the Figma "chrome-mockup" file
  (file key KVZcQgocc6VrPghKVOBGye) so it scales crisply at any width.

  Aspect: design is 931×615 (~1.514 ratio) with an 86px header. The header is
  rendered at a fixed pixel height (so icons stay sharp); the content slot
  fills the remainder.
-->
<div
  class="chrome-frame chrome-frame--{size} {className}"
  class:chrome-frame--flat={flat}
  style="--cf-border: {palette.border}; --cf-header-bg: {palette.headerBg}; --cf-tabs-from: {palette.tabsGradFrom}; --cf-tabs-to: {palette.tabsGradTo}; --cf-active-tab-bg: {palette.activeTabBg}; --cf-base-bg: {palette.baseBg}; --cf-base-border: {palette.baseBorder}; --cf-url-bg: {palette.urlBg}; --cf-url-text: {palette.urlText}; --cf-tab-text: {palette.tabText}; --cf-icon: {palette.icon}; --cf-icon-muted: {palette.iconMuted}; --cf-divider: {palette.divider}; --cf-content-bg: {palette.contentBg};"
>
  <div class="chrome-header">
    <!-- Header top: tab row + window controls -->
    <div class="chrome-header-top">
      <div class="chrome-tabs">
        <!-- Bookend (sidebar/chevron toggle) -->
        <div class="chrome-bookend">
          <div class="chrome-bookend-button">
            <svg viewBox="0 0 18 18" width="18" height="18" aria-hidden="true">
              <path d="M6.5 5.5l3 3 3-3" stroke="var(--cf-icon)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none" />
            </svg>
          </div>
        </div>

        <!-- Active tab -->
        <div class="chrome-tab chrome-tab--active">
          <span class="chrome-tab-icon">
            <svg viewBox="0 0 18 18" width="14" height="14" aria-hidden="true">
              <circle cx="9" cy="9" r="7.5" fill="#fff" stroke="var(--cf-icon)" stroke-width="1" />
              <text x="9" y="12.5" text-anchor="middle" font-family="Arial, sans-serif" font-size="9" font-weight="700" fill="#4285f4">G</text>
            </svg>
          </span>
          <span class="chrome-tab-title">{tabTitle}</span>
          <span class="chrome-tab-close">
            <svg viewBox="0 0 18 18" width="10" height="10" aria-hidden="true">
              <path d="M5 5l8 8M13 5l-8 8" stroke="var(--cf-icon)" stroke-width="1.5" stroke-linecap="round" />
            </svg>
          </span>
        </div>

        {#if secondTab}
          <!-- Inactive tab -->
          <div class="chrome-tab chrome-tab--inactive">
            <span class="chrome-tab-icon">
              <svg viewBox="0 0 18 18" width="14" height="14" aria-hidden="true">
                <circle cx="9" cy="9" r="7" fill="none" stroke="var(--cf-icon)" stroke-width="1" />
                <circle cx="9" cy="9" r="2.5" fill="var(--cf-icon)" />
              </svg>
            </span>
            <span class="chrome-tab-title">{secondTab}</span>
            <span class="chrome-tab-close">
              <svg viewBox="0 0 18 18" width="10" height="10" aria-hidden="true">
                <path d="M5 5l8 8M13 5l-8 8" stroke="var(--cf-icon)" stroke-width="1.5" stroke-linecap="round" />
              </svg>
            </span>
            <span class="chrome-divider"></span>
            <span class="chrome-tab-plus">
              <svg viewBox="0 0 18 18" width="12" height="12" aria-hidden="true">
                <path d="M9 4.5v9M4.5 9h9" stroke="var(--cf-icon)" stroke-width="1.5" stroke-linecap="round" />
              </svg>
            </span>
          </div>
        {/if}
      </div>

      <!-- Window controls (Windows-style, right) -->
      <div class="chrome-window-controls">
        <button class="chrome-wc" type="button" aria-label="Minimize">
          <svg viewBox="0 0 18 18" width="10" height="10" aria-hidden="true">
            <line x1="4" y1="9" x2="14" y2="9" stroke="var(--cf-icon-muted)" stroke-width="1" />
          </svg>
        </button>
        <button class="chrome-wc" type="button" aria-label="Maximize">
          <svg viewBox="0 0 18 18" width="10" height="10" aria-hidden="true">
            <rect x="4" y="4" width="10" height="10" fill="none" stroke="var(--cf-icon-muted)" stroke-width="1" />
          </svg>
        </button>
        <button class="chrome-wc" type="button" aria-label="Close">
          <svg viewBox="0 0 18 18" width="10" height="10" aria-hidden="true">
            <path d="M4 4l10 10M14 4l-10 10" stroke="var(--cf-icon-muted)" stroke-width="1" stroke-linecap="round" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Address bar -->
    <div class="chrome-header-base">
      <div class="chrome-nav-icons">
        <span class="chrome-nav-btn" aria-hidden="true">
          <svg viewBox="0 0 18 18" width="16" height="16">
            <path d="M11 4l-5 5 5 5" stroke="var(--cf-icon)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none" />
          </svg>
        </span>
        <span class="chrome-nav-btn" aria-hidden="true">
          <svg viewBox="0 0 18 18" width="16" height="16">
            <path d="M7 4l5 5-5 5" stroke="var(--cf-icon)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none" />
          </svg>
        </span>
        <span class="chrome-nav-btn" aria-hidden="true">
          <svg viewBox="0 0 18 18" width="16" height="16">
            <path d="M14 9a5 5 0 1 1-1.46-3.54M14 4v3.5h-3.5" stroke="var(--cf-icon)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none" />
          </svg>
        </span>
      </div>

      <div class="chrome-url">
        {#if secure}
          <span class="chrome-url-lock" aria-hidden="true">
            <svg viewBox="0 0 18 18" width="12" height="12">
              <rect x="4.5" y="8" width="9" height="6.5" rx="1" fill="var(--cf-icon)" />
              <path d="M6 8V5.75a3 3 0 0 1 6 0V8" stroke="var(--cf-icon)" stroke-width="1.3" fill="none" stroke-linecap="round" />
            </svg>
          </span>
        {/if}
        <span class="chrome-url-text">{url}</span>
        <span class="chrome-url-icons" aria-hidden="true">
          <svg viewBox="0 0 18 18" width="14" height="14">
            <path d="M9 2.5l1.95 4.05 4.55.55-3.3 3.05.85 4.4L9 12.5l-4.05 2.05.85-4.4-3.3-3.05 4.55-.55z" fill="none" stroke="var(--cf-icon)" stroke-width="1.2" stroke-linejoin="round" />
          </svg>
        </span>
      </div>

      <div class="chrome-right-icons" aria-hidden="true">
        <svg viewBox="0 0 18 18" width="16" height="16">
          <!-- Jigsaw / extensions glyph -->
          <path d="M5.5 4h2.2v1.3a1 1 0 0 0 2 0V4h2.2v2.2H13a1 1 0 0 0 0 2h-1.1V12H10v-1.1a1 1 0 0 0-2 0V12H5.5V9.7H4.6a1 1 0 0 0 0-2h.9V4z" fill="none" stroke="var(--cf-icon)" stroke-width="1.2" stroke-linejoin="round" />
        </svg>
        <span class="chrome-divider chrome-divider--vert"></span>
        <svg viewBox="0 0 18 18" width="16" height="16">
          <circle cx="9" cy="9" r="7" fill="none" stroke="var(--cf-icon)" stroke-width="1.2" />
          <circle cx="9" cy="7.2" r="2.2" fill="var(--cf-icon)" />
          <path d="M3.5 14.5c1-2 3-3 5.5-3s4.5 1 5.5 3" stroke="var(--cf-icon)" stroke-width="1.2" fill="none" />
        </svg>
        <svg viewBox="0 0 18 18" width="16" height="16">
          <circle cx="9" cy="4" r="1.2" fill="var(--cf-icon)" />
          <circle cx="9" cy="9" r="1.2" fill="var(--cf-icon)" />
          <circle cx="9" cy="14" r="1.2" fill="var(--cf-icon)" />
        </svg>
      </div>
    </div>
  </div>

  <!-- Content area -->
  <div class="chrome-content">
    {@render children()}
  </div>
</div>

<style>
  .chrome-frame {
    position: relative;
    width: 100%;
    border: 1px solid var(--cf-border);
    border-radius: 8px;
    background: var(--cf-content-bg);
    overflow: hidden;
    box-shadow:
      0 0 8px rgba(0, 0, 0, 0.2),
      0 32px 64px rgba(0, 0, 0, 0.24);
    display: flex;
    flex-direction: column;
    /* Fonts: Chrome ships SF/Segoe; closest cross-platform is system-ui. */
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    color: var(--cf-tab-text);
    /* Per-size knobs — see chrome-frame--sm/md below. */
    --cf-row-h: 43px;
    --cf-tab-font: 12px;
    --cf-active-tab-w: 233px;
    --cf-inactive-tab-w: 254px;
    --cf-wc-w: 38px;
    --cf-wc-w-last: 40px;
    --cf-icon-scale: 1;
  }

  .chrome-frame--sm {
    --cf-row-h: 32px;
    --cf-tab-font: 11px;
    --cf-active-tab-w: 188px;
    --cf-inactive-tab-w: 196px;
    --cf-wc-w: 28px;
    --cf-wc-w-last: 30px;
    --cf-icon-scale: 0.85;
    border-radius: 6px;
  }

  .chrome-frame :global(svg) {
    transform: scale(var(--cf-icon-scale));
    transform-origin: center;
  }

  .chrome-frame--flat {
    box-shadow: none;
  }

  /* Header is a fixed pixel height so icons stay sharp. */
  .chrome-header {
    flex: 0 0 auto;
    background: var(--cf-header-bg);
    display: flex;
    flex-direction: column;
  }

  .chrome-header-top {
    position: relative;
    height: var(--cf-row-h);
    background: var(--cf-header-bg);
    display: flex;
    align-items: flex-end;
    overflow: hidden;
  }

  .chrome-tabs {
    display: flex;
    align-items: flex-end;
    height: 100%;
    background: var(--cf-header-bg);
  }

  .chrome-bookend {
    width: 40px;
    height: calc(var(--cf-row-h) - 4px);
    background: var(--cf-header-bg);
    border-bottom-right-radius: 11px;
    position: relative;
  }

  .chrome-bookend-button {
    position: absolute;
    left: 5px;
    top: 0;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .chrome-tab {
    height: calc(var(--cf-row-h) - 4px);
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 12px;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    overflow: hidden;
    position: relative;
    flex-shrink: 0;
    font-size: var(--cf-tab-font);
  }

  .chrome-tab--active {
    background: var(--cf-active-tab-bg);
    width: var(--cf-active-tab-w);
  }

  .chrome-tab--inactive {
    background: var(--cf-header-bg);
    width: var(--cf-inactive-tab-w);
    border-bottom-left-radius: 8px;
  }

  .chrome-tab-icon {
    display: inline-flex;
    flex-shrink: 0;
  }

  .chrome-tab-title {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--cf-tab-text);
  }

  .chrome-tab-close,
  .chrome-tab-plus {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }

  .chrome-divider {
    display: inline-block;
    width: 1px;
    height: 18px;
    background: var(--cf-divider);
    flex-shrink: 0;
  }

  .chrome-divider--vert {
    margin: 0 2px;
  }

  .chrome-window-controls {
    position: absolute;
    right: 0;
    top: 0;
    display: flex;
    height: var(--cf-row-h);
    background: var(--cf-header-bg);
  }

  .chrome-wc {
    width: var(--cf-wc-w);
    height: var(--cf-row-h);
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 0;
    cursor: default;
    padding: 0;
  }

  .chrome-wc:last-child {
    width: var(--cf-wc-w-last);
  }

  /* Address bar */
  .chrome-header-base {
    height: var(--cf-row-h);
    background: var(--cf-base-bg);
    border-bottom: 1px solid var(--cf-base-border);
    border-top-left-radius: 11px;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 12px;
  }

  .chrome-nav-icons {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  .chrome-nav-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
  }

  .chrome-url {
    flex: 1;
    min-width: 0;
    height: 28px;
    background: var(--cf-url-bg);
    border-radius: 64px;
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 0 8px 0 14px;
    font-size: 12px;
    color: var(--cf-url-text);
  }

  .chrome-url-lock {
    display: inline-flex;
    flex-shrink: 0;
  }

  .chrome-url-text {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .chrome-url-icons {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
  }

  .chrome-right-icons {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  .chrome-content {
    flex: 1;
    min-height: 0;
    background: var(--cf-content-bg);
    overflow: hidden;
    position: relative;
  }
</style>
